// Suggested Ask replies are computed from the same scoped demo cases as the UI.
// Arbitrary questions still use the static-demo unavailable message.
import { askEnglish, askGerman, askItalian } from "../../globals/i18n/ask";
import { english as watchEnglish } from "../../globals/i18n/watchlist/english";
import { german as watchGerman } from "../../globals/i18n/watchlist/german";
import { italian as watchItalian } from "../../globals/i18n/watchlist/italian";
import type { Process } from "../openapi-aliases";
import type { components } from "../openapi/company-router";

type Filters = components["schemas"]["ChatScopeFilters"];
type Question =
  | "duration"
  | "steps"
  | "rework"
  | "process_people"
  | "patterns"
  | "different_steps"
  | "people_variation"
  | "session_summary"
  | "session_processes"
  | "session_time"
  | "investigate"
  | "automation";
const questions: Question[] = [
  "duration",
  "steps",
  "rework",
  "process_people",
  "patterns",
  "different_steps",
  "people_variation",
  "session_summary",
  "session_processes",
  "session_time",
  "investigate",
  "automation",
];
const normalize = (text: string) =>
  text
    .trim()
    .toLowerCase()
    .replace(/[?.!]+$/, "");
const counted = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;
const minutes = (seconds: number) => `${(seconds / 60).toFixed(1)} min`;
const includes = (scope: string[] | null | undefined, value: string) =>
  scope == null || scope.includes(value);

export function demoScopedAnswer(
  processes: Process[],
  filters: Filters,
  text: string,
): string | null {
  const question =
    questions.find(key =>
      [askEnglish, askGerman, askItalian].some(locale =>
        key === "duration"
          ? processes.some(
              process =>
                normalize(locale[key].replace("{{process}}", process.name)) === normalize(text),
            )
          : normalize(locale[key]) === normalize(text),
      ),
    ) ??
    [watchEnglish, watchGerman, watchItalian]
      .flatMap(
        locale =>
          [
            [locale.suggestion_apps, "apps"],
            [locale.suggestion_rework, "rework"],
            [locale.suggestion_automation, "automation"],
          ] as const,
      )
      .find(([prompt]) => normalize(prompt) === normalize(text))?.[1];
  if (!question) return null;
  const scoped = processes
    .filter(process => includes(filters.processes, process.name))
    .map(process => ({
      ...process,
      instances: process.instances.filter(
        instance =>
          includes(filters.instanceIds, instance.id) &&
          includes(filters.recordingIds, instance.source.recordingId) &&
          includes(filters.userIds, instance.source.userId) &&
          (!filters.from || Date.parse(instance.source.traceStart) >= Date.parse(filters.from)) &&
          (!filters.to || Date.parse(instance.source.traceStart) < Date.parse(filters.to)),
      ),
    }))
    .filter(process => process.instances.length > 0);
  const instances = scoped.flatMap(process => process.instances);
  if (!instances.length)
    return "No demo cases match the selected people, dates, sessions and cases. Widen the scope to explore this sample.";
  const recordings = new Set(instances.map(instance => instance.source.recordingId));
  const users = new Set(instances.map(instance => instance.source.userId));
  const intro = `This selection contains **${counted(instances.length, "case")}** across **${counted(recordings.size, "session")}** and **${counted(users.size, "person", "people")}**. These figures describe the selected demo observations.`;
  const evidence = scoped
    .map(process => `[${process.name}](/processes/${process.slug}/)`)
    .join(", ");
  const finish = (answer: string) => `${intro}\n\n${answer}\n\nExplore the cases: ${evidence}.`;
  if (question === "apps") {
    const apps = new Map<string, number>();
    for (const instance of instances)
      for (const step of instance.steps) {
        for (const [name, seconds] of Object.entries(step.appSeconds ?? {}))
          apps.set(name, (apps.get(name) ?? 0) + seconds);
      }
    return finish(
      `Measured app time in the selected process cases:\n\n${[...apps]
        .sort((a, b) => b[1] - a[1])
        .map(([name, seconds]) => `- **${name}**: ${minutes(seconds)}.`)
        .join("\n")}\n\nThis covers measured process activity, not all captured time.`,
    );
  }
  if (
    question === "duration" ||
    question === "steps" ||
    question === "rework" ||
    question === "automation"
  ) {
    const steps = new Map<string, { seconds: number; cases: number; repeats: number }>();
    for (const process of scoped) {
      const names = new Map(process.nodes.map(node => [node.id, node.name]));
      for (const instance of process.instances) {
        const seen = new Set<string>();
        for (const step of instance.steps) {
          const name = names.get(step.nodeId) ?? step.nodeId;
          if (filters.selectedSteps?.length && !filters.selectedSteps.includes(name)) continue;
          const entry = steps.get(name) ?? { seconds: 0, cases: 0, repeats: 0 };
          entry.seconds += step.duration;
          if (seen.has(name)) entry.repeats += 1;
          else entry.cases += 1;
          seen.add(name);
          steps.set(name, entry);
        }
      }
    }
    const ranked = [...steps].sort((a, b) =>
      question === "rework" ? b[1].repeats - a[1].repeats : b[1].seconds - a[1].seconds,
    );
    if (question === "rework") {
      const repeated = ranked.filter(([, value]) => value.repeats > 0).slice(0, 5);
      return finish(
        repeated.length
          ? `Repeated steps within a case:\n\n${repeated.map(([name, value]) => `- **${name}**: ${value.repeats} additional occurrences; present in ${value.cases} cases.`).join("\n")}\n\nRepetition can be legitimate work. Inspect the case before treating it as avoidable rework.`
          : "No step names repeat within the selected cases. This does not rule out rework outside this sample.",
      );
    }
    return finish(
      `Steps with the most observed active time:\n\n${ranked
        .slice(0, 5)
        .map(
          ([name, value]) =>
            `- **${name}**: ${minutes(value.seconds)} across ${value.cases} cases.`,
        )
        .join(
          "\n",
        )}\n\n${question === "automation" ? "These are candidates to inspect for routine inputs and repeatable decisions. Time alone cannot establish automation feasibility or savings." : "Step time identifies where effort is spent; it does not by itself explain waiting or prove inefficiency."}`,
    );
  }
  if (
    question === "process_people" ||
    question === "different_steps" ||
    question === "people_variation"
  ) {
    const comparisons = scoped.map(process => {
      const people = [...new Set(process.instances.map(instance => instance.source.userId))].map(
        userId => {
          const cases = process.instances.filter(instance => instance.source.userId === userId);
          const paths = new Set(
            cases.map(instance => instance.steps.map(step => step.nodeId).join(",")),
          );
          return `- **${cases[0].source.userName ?? userId}**: ${cases.length} cases, ${minutes(cases.reduce((sum, instance) => sum + instance.source.avgDuration, 0) / cases.length)} active time per case, ${paths.size} observed paths.`;
        },
      );
      return `**${process.name}**\n\n${people.join("\n")}${people.length === 1 ? "\n\nOnly one person is represented, so this selection cannot support a comparison between people." : ""}`;
    });
    return finish(
      `${comparisons.join("\n\n")}\n\nCase mix and sample size can explain differences. These observations are not a performance ranking.`,
    );
  }
  const totals = scoped
    .map(process => ({
      process,
      seconds: process.instances.reduce((sum, instance) => sum + instance.source.avgDuration, 0),
    }))
    .sort((a, b) => b.seconds - a.seconds);
  return finish(
    `Observed process activity:\n\n${totals.map(({ process, seconds }) => `- **${process.name}**: ${process.instances.length} cases, ${minutes(seconds)} active time, ${minutes(seconds / process.instances.length)} per case.`).join("\n")}\n\n${question === "investigate" ? "Start by inspecting the largest time contribution, separating case volume from time per case. A large share alone does not establish an improvement opportunity." : "These totals cover identified process cases; they do not represent every minute captured in the sessions."}`,
  );
}
